import React, { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import StateParkStats from './components/StateParkStats';
import DidYouKnowSection from './components/DidYouKnowSection';
import NewsletterSignup from './components/NewsletterSignup';
import PoweredBySection from './components/PoweredBySection';
import SEOHead from '../../components/SEOHead';
import { generateOrganizationStructuredData, generateFAQStructuredData } from '../../utils/seoUtils';

const LandingPage = () => {
  const { user, userProfile, isAuthenticated, loading } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQ structured data for landing page
  const faqData = [
    {
      question: "How does the AI trip planner work?",
      answer: "Our AI analyzes your preferences, travel dates, and interests to create personalized state park itineraries. Simply answer a few questions about your ideal trip, and our AI will generate a complete day-by-day plan with activities, routes, and recommendations."
    },
    {
      question: "Is ParkTrip Planner AI free to use?",
      answer: "Yes! ParkTrip Planner AI is completely free. Create unlimited trip itineraries, save your plans, and access all features at no cost."
    },
    {
      question: "Can I modify the AI-generated itinerary?",
      answer: "Absolutely! You can edit any part of your itinerary, add custom activities, adjust timing, and save multiple versions of your trip plan."
    },
    {
      question: "Which state parks are covered?",
      answer: "We cover state parks across all 50 US states. Our database includes thousands of parks with detailed information about trails, activities, facilities, and seasonal recommendations."
    }
  ];

  const organizationData = generateOrganizationStructuredData();
  const faqStructuredData = generateFAQStructuredData(faqData);

  // Combine structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [organizationData, faqStructuredData]
  };

  // CRITICAL FIX: Show loading during auth initialization AND logout completion
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        pageType="home"
        title="AI State Park Trip Planner | Plan Your Perfect Outdoor Adventure"
        description="Plan your perfect state park adventure with AI-powered itineraries. Get personalized recommendations for hiking, camping, and outdoor activities across all 50 US states."
        ogImage="https://parktripplannerai.com/og-image.jpg"
        keywords="state park planner, AI trip planner, outdoor adventure planning, camping itinerary, hiking trips, national parks, state park trips, AI travel assistant, outdoor recreation, trip planning tool"
        canonicalUrl="https://parktripplannerai.com/"
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <HeroSection isAuthenticated={isAuthenticated} user={user} userProfile={userProfile} />
          <StateParkStats />
          <NewsletterSignup />
          <DidYouKnowSection />
          <PoweredBySection />
        </main>
      </div>
    </>
  );
};

export default LandingPage;